import {
  Activity,
  AudioLines,
  ChevronDown,
  FolderKanban,
  Library,
  ListMusic,
  LogOut,
  Settings,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { Banner, Button } from '../design-system/index.js';
import { CompactNowPlaying, MobilePlayerHeaderContent } from '../player/now-playing-bar.js';
import { AppErrorBoundary } from './app-error-boundary.js';
import { useWorkspace, type OperatorSession } from './runtime.js';

type NavigationItem = {
  href: string;
  label: string;
  icon: LucideIcon;
};

const navigation: readonly NavigationItem[] = [
  { href: '/create', label: 'Create', icon: Sparkles },
  { href: '/projects', label: 'Projects', icon: FolderKanban },
  { href: '/catalog', label: 'Catalog', icon: AudioLines },
  { href: '/library', label: 'Library', icon: Library },
  { href: '/playlists', label: 'Playlists', icon: ListMusic },
  { href: '/activity', label: 'Activity', icon: Activity },
  { href: '/settings', label: 'Settings', icon: Settings },
];

const mobileNavigation = navigation.filter(({ href }) => href !== '/activity');
const desktopLayoutQuery = '(min-width: 1024px)';

function subscribeToDesktopLayout(onChange: () => void) {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => undefined;
  }
  const mediaQuery = window.matchMedia(desktopLayoutQuery);
  mediaQuery.addEventListener('change', onChange);
  return () => mediaQuery.removeEventListener('change', onChange);
}

function getDesktopLayoutSnapshot() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(desktopLayoutQuery).matches
  );
}

function useDesktopLayout() {
  return useSyncExternalStore(subscribeToDesktopLayout, getDesktopLayoutSnapshot, () => false);
}

function Brand({ onNavigate }: { onNavigate: (() => void) | undefined }) {
  return (
    <NavLink
      to="/create"
      onClick={onNavigate}
      className="inline-flex min-h-11 items-center gap-3 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-accent-ring"
      aria-label="Song Composer home"
    >
      <span className="grid size-9 place-items-center rounded-lg bg-accent font-mono text-sm font-semibold text-accent-ink">
        5
      </span>
      <span className="font-semibold tracking-tight text-ink">Song Composer</span>
    </NavLink>
  );
}

function Navigation({ onNavigate }: { onNavigate: (() => void) | undefined }) {
  return (
    <nav aria-label="Primary" className="grid gap-1">
      {navigation.map(({ href, label, icon: Icon }) => (
        <NavLink
          key={href}
          to={href}
          onClick={onNavigate}
          className={({ isActive }) =>
            [
              'flex min-h-11 items-center gap-3 rounded-md px-3 text-sm font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-accent-ring motion-reduce:transition-none',
              isActive
                ? 'border-l-2 border-accent bg-accent-soft text-accent'
                : 'border-l-2 border-transparent text-ink-2 hover:bg-surface hover:text-ink',
            ].join(' ')
          }
        >
          <Icon className="size-[1.125rem]" strokeWidth={1.8} aria-hidden="true" />
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

function MobileNavigation({ onNavigate }: { onNavigate: () => void }) {
  return (
    <nav aria-label="Primary" className="grid grid-cols-6 gap-1">
      {mobileNavigation.map(({ href, label, icon: Icon }) => (
        <NavLink
          key={href}
          to={href}
          onClick={onNavigate}
          className={({ isActive }) =>
            [
              'grid min-h-16 min-w-0 place-items-center content-center gap-1 rounded-md px-0.5 text-[0.625rem] font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-accent-ring motion-reduce:transition-none',
              isActive
                ? 'bg-accent-soft text-accent'
                : 'text-ink-2 hover:bg-surface hover:text-ink',
            ].join(' ')
          }
        >
          <Icon className="size-5" strokeWidth={1.8} aria-hidden="true" />
          <span className="max-w-full truncate">{label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function SignOutButton({ onSignOut }: { onSignOut: () => Promise<void> }) {
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signOut = async () => {
    setPending(true);
    setError(null);
    try {
      await onSignOut();
      navigate('/login', { replace: true });
    } catch {
      setError('Sign out failed. Try again.');
    } finally {
      setPending(false);
    }
  };

  return (
    <div>
      <Button
        variant="ghost"
        onClick={() => void signOut()}
        disabled={pending}
        className="w-full justify-start px-3 text-ink-2"
      >
        <LogOut className="size-[1.125rem]" aria-hidden="true" />
        {pending ? 'Signing out…' : 'Sign out'}
      </Button>
      {error ? (
        <p className="mt-2 px-3 text-xs text-err" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function DesktopRail({ onSignOut }: { onSignOut: () => Promise<void> }) {
  return (
    <aside className="fixed inset-y-0 left-0 z-20 flex w-56 flex-col overflow-y-auto border-r border-border bg-bg-2 px-4 py-5">
      <div className="px-2">
        <Brand onNavigate={undefined} />
      </div>
      <div className="mt-8">
        <Navigation onNavigate={undefined} />
      </div>
      <div className="mt-auto space-y-3">
        <CompactNowPlaying layout="rail" />
        <SignOutButton onSignOut={onSignOut} />
      </div>
    </aside>
  );
}

function MobileHeader() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header className="mobile-app-header sticky top-0 z-30 bg-bg-2/95 backdrop-blur">
      <MobilePlayerHeaderContent />
      {open ? (
        <div
          id="mobile-primary-navigation"
          className="pt-2 pr-[max(0.75rem,env(safe-area-inset-right))] pb-2 pl-[max(0.75rem,env(safe-area-inset-left))]"
        >
          <MobileNavigation onNavigate={() => setOpen(false)} />
        </div>
      ) : null}
      <div className="relative h-1.5">
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-full h-4">
          <span className="absolute top-0 right-[calc(50%+2rem)] left-0 border-t border-border" />
          <span className="absolute top-0 right-0 left-[calc(50%+2rem)] border-t border-border" />
          <svg
            className="absolute -top-px left-1/2 h-[17px] w-16 -translate-x-1/2 overflow-visible"
            viewBox="0 -1 64 17"
            fill="none"
          >
            <path
              d="M0 -1 H64 V0.5 H57 L53 4.5 V8.5 Q53 15.5 46 15.5 H18 Q11 15.5 11 8.5 V4.5 L7 0.5 H0 Z"
              fill="var(--bg-2)"
            />
            <path
              d="M0 0.5 H7 L11 4.5 V8.5 Q11 15.5 18 15.5 H46 Q53 15.5 53 8.5 V4.5 L57 0.5 H64"
              fill="none"
              stroke="var(--border)"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="absolute -top-4 left-1/2 z-10 h-11 min-h-11 w-[4.5rem] -translate-x-1/2 rounded-none border-0 bg-transparent p-0 text-ink-2 hover:bg-transparent hover:text-ink focus-visible:ring-2 focus-visible:ring-accent-ring"
          aria-controls="mobile-primary-navigation"
          aria-expanded={open}
          aria-label={open ? 'Close navigation' : 'Open navigation'}
          onClick={() => setOpen((current) => !current)}
        >
          <ChevronDown
            aria-hidden="true"
            strokeWidth={2.25}
            className={`!size-3.5 transition-transform motion-reduce:transition-none ${open ? 'rotate-180' : ''}`}
          />
        </Button>
      </div>
    </header>
  );
}

function ConnectionBanner() {
  const { connection } = useWorkspace();
  if (connection.status === 'active') return null;

  const needsAuthentication = connection.status === 'reauthentication_required';
  return (
    <div className="border-b border-border bg-bg px-4 py-3 lg:pl-[15rem] lg:pr-6">
      <Banner
        tone={needsAuthentication ? 'warning' : 'danger'}
        title={needsAuthentication ? 'Connection needs reauthentication' : 'Connection disabled'}
      >
        {needsAuthentication
          ? 'Catalog browsing remains available, but provider actions cannot continue until the connection is restored.'
          : 'Provider actions are unavailable for this workspace.'}
      </Banner>
    </div>
  );
}

export function AppShell({
  session,
  onSignOut,
}: {
  session: OperatorSession;
  onSignOut: () => Promise<void>;
}) {
  void session;
  const location = useLocation();
  const desktopLayout = useDesktopLayout();
  return (
    <div className="min-h-svh bg-bg text-ink">
      {desktopLayout ? <DesktopRail onSignOut={onSignOut} /> : <MobileHeader />}
      <ConnectionBanner />
      <main className="min-w-0 lg:pl-56">
        <div className="app-page mx-auto w-full max-w-6xl py-6 sm:py-8 lg:py-10">
          <AppErrorBoundary
            key={location.pathname}
            layout="page"
            onReset={() => window.location.reload()}
          >
            <Outlet />
          </AppErrorBoundary>
        </div>
      </main>
    </div>
  );
}
